import React, { useState, useContext, useEffect, useRef } from 'react';
import { ApplicationContext, AppContext } from '../../context/Contexts';
import { Card, CardContent, Typography } from '@material-ui/core';
import { withSnackbar } from 'notistack';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { EmbedTokenResponse } from '../../interfaces/PowerBi';
import { useParams } from 'react-router';

const PowerBiReport = () => {
  const { reportCode } = useParams<{ reportCode: string }>();
  const [embedToken, setEmbedToken] = useState<EmbedTokenResponse | null>(null);
  const [activity, setActivity] = useState<boolean>(true);
  const [showTransparentScreen, setShowTransparentScreen] = useState<boolean>(false);
  const [waitingForCapacity, setWaitingForCapacity] = useState<boolean>(false);
  const context = useContext(ApplicationContext) as AppContext;
  const { translatorService, powerBiService } = context;
  // References to report objects
  const reportObjRef = useRef<any>(null);
  const timeToUpdate = 60000; // 1 minute before expiration

  useEffect(() => {
    getReportEmbedToken();
  }, []);

  useEffect(() => {
    document.addEventListener('click', activityDetected);
    document.addEventListener('keyup', activityDetected);
    return () => {
      document.removeEventListener('click', activityDetected);
      document.removeEventListener('keyup', activityDetected);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      await refreshTokenIfNeeded();
    }, timeToUpdate);
    return () => clearInterval(interval);
  }, [embedToken, activity]);

  const getReportEmbedToken = async () => {
    if (!reportObjRef.current) {
      return;
    }
    try {
      const response = await powerBiService.GetEmbedToken(
        reportCode,
        activity
      );
      if (reportObjRef.current) {
        console.log('Set new access token for report');
        reportObjRef.current.setAccessToken(response.token);
      }
      console.log('Embed token:', response);
      setEmbedToken(response);
      setActivity(false);
    } catch (error) {
      console.error('Error fetching embed token, retrying...', error);
      await getReportEmbedToken();
    }
  };

  const refreshTokenIfNeeded = async () => {
    if (!reportObjRef.current || !embedToken) {
      return;
    }
    const now = Date.now();
    const expiration = Date.parse(embedToken.expiration);
    const timeUntilExpiration = expiration - now;
    if (timeUntilExpiration < timeToUpdate) {
      await getReportEmbedToken();
    }
    console.log('Has activity:', activity);
    if (!activity) {
      setShowTransparentScreen(true);
    }
  };

  const startCapacity = async () => {
    setWaitingForCapacity(true);
    try {
      await powerBiService.StartCapacity();
      setWaitingForCapacity(false);
      setShowTransparentScreen(false);
    } catch (error) {
      await startCapacity();
    }
  };

  const clickOnTransparentScreen = async () => {
    setWaitingForCapacity(true);
    const capacityStatus = await powerBiService.GetCapacityStatus();
    if (capacityStatus !== "Active") {
      await startCapacity();
    } else {
      setWaitingForCapacity(false);
      setShowTransparentScreen(false);
    }
  };

  const activityDetected = () => {
    console.log('Activity detected');
    setActivity(true);
  };
  
  return (
    <Card className="card-box w-100 shadow-none">
      <div className="card-header text-center">
        <div className="card-header--title">
          <Typography variant="h4">
            {translatorService.Tranlate('EMBED_POWER_BI_ORDERS_TITLE', 'Embed Power Bi Report')}
          </Typography>
        </div>
      </div>
      <CardContent>
        <PowerBIEmbed
          embedConfig={{
            type: 'report',
            embedUrl: embedToken?.embedUrl,
            tokenType: models.TokenType.Embed,
            accessToken: embedToken?.token
          }}
          eventHandlers={
            new Map([
              ['loaded', () => { console.log('Report loaded'); }],
              ['rendered', () => { console.log('Report rendered'); }],
              ['error', (event: any) => { console.log(event.detail); }],
              ['visualClicked', () => { console.log('Visual clicked'); activityDetected(); }],
              ['buttonClicked', () => { console.log('Button clicked'); activityDetected(); }],
              ['commandTriggered', () => { console.log('Command triggered'); activityDetected(); }],
              ['dataSelected', () => { console.log('Data selected'); activityDetected(); }],
              ['dataHyperlinkClicked', () => { console.log('Data hyperlink clicked'); activityDetected(); }],
              ['pageChanged', () => { console.log('Page changed'); activityDetected(); }],
              ['selectionChanged', () => { console.log('Selection changed'); activityDetected(); }],
            ])
          }
          cssClassName={"reportClass"}
          getEmbeddedComponent={(embeddedReport) => {
            console.log('Report Embedded', embeddedReport);
            reportObjRef.current = embeddedReport;
          }}
        />
        {showTransparentScreen && (
          <div
            className={`transparent-screen ${waitingForCapacity ? "wait" : ""}`}
            onClick={clickOnTransparentScreen}
          >
          </div>
        )}
      </CardContent>
    </Card>
  );
};
export default withSnackbar(PowerBiReport);